import { useState } from "react";
import { AXIS_TEXT, GRID, Tooltip, niceScale, useWidth, type ChartSeries, type Tip } from "./chartKit";

interface Props {
  categories: string[];
  series: (ChartSeries & { values: number[] })[];
  ariaLabel: string;
  height?: number;
  /** Print each column's value on its cap (for a single series; grouped columns leave the values to the tooltip and table). */
  labelValues?: boolean;
}

const M = { top: 12, right: 8, bottom: 26, left: 32 };
const BAR_MAX = 24, GAP = 2, RADIUS = 4;

/** A column with a 4px rounded top and a square base. */
function barPath(x: number, y: number, w: number, h: number): string {
  const r = Math.min(RADIUS, w / 2, h);
  return h <= 0 ? "" : `M${x},${y + h} V${y + r} Q${x},${y} ${x + r},${y} H${x + w - r} Q${x + w},${y} ${x + w},${y + r} V${y + h} Z`;
}

/** Grouped columns on one baseline: thin (24px at most), 2px apart, the hovered or focused group lifts and shows one tooltip. */
export default function ColumnChart({ categories, series, ariaLabel, height = 220, labelValues }: Props) {
  const [ref, width] = useWidth();
  const [active, setActive] = useState<number | null>(null);
  const inner = { w: Math.max(40, width - M.left - M.right), h: height - M.top - M.bottom };
  const max = Math.max(0, ...series.flatMap((s) => s.values));
  const { top, ticks } = niceScale(max);
  const band = inner.w / Math.max(1, categories.length);
  const barW = Math.min(BAR_MAX, Math.max(4, (band * 0.7 - GAP * (series.length - 1)) / series.length));
  const groupW = barW * series.length + GAP * (series.length - 1);
  const y = (v: number) => M.top + inner.h - (v / top) * inner.h;
  const tipFor = (i: number): Tip => ({
    x: M.left + band * i + band / 2, title: categories[i],
    rows: series.map((s) => ({ color: s.color, label: s.label, value: String(s.values[i]) })),
  });
  const every = Math.ceil(categories.length / Math.max(1, Math.floor(inner.w / 56)));      // thin the x labels when they would collide

  return (
    <div ref={ref} className="relative">
      <svg width={width} height={height} role="img" aria-label={ariaLabel} onPointerLeave={() => setActive(null)}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={M.left} x2={width - M.right} y1={y(t)} y2={y(t)} stroke={GRID} strokeWidth={1} />
            <text x={M.left - 6} y={y(t)} textAnchor="end" dominantBaseline="middle" fontSize={11} fill={AXIS_TEXT}>{t}</text>
          </g>
        ))}
        {categories.map((c, i) => {
          const left = M.left + band * i + (band - groupW) / 2;
          return (
            <g key={c + i}>
              {series.map((s, si) => {
                const h = (s.values[i] / top) * inner.h;
                const x = left + si * (barW + GAP);
                return (
                  <g key={s.key}>
                    <path d={barPath(x, y(s.values[i]), barW, h)} fill={s.color} opacity={active === null || active === i ? 1 : 0.55} />
                    {labelValues && s.values[i] > 0 && <text x={x + barW / 2} y={y(s.values[i]) - 5} textAnchor="middle" fontSize={11} fontWeight={600} fill="hsl(var(--foreground))">{s.values[i]}</text>}
                  </g>
                );
              })}
              {i % every === 0 && <text x={M.left + band * i + band / 2} y={height - 8} textAnchor="middle" fontSize={11} fill={AXIS_TEXT}>{c}</text>}
              {/* the hit target is the whole band, far larger than the painted columns */}
              <rect x={M.left + band * i} y={M.top} width={band} height={inner.h} fill="transparent" tabIndex={0}
                aria-label={`${c}: ${series.map((s) => `${s.label} ${s.values[i]}`).join(", ")}`}
                onPointerMove={() => setActive(i)} onFocus={() => setActive(i)} onBlur={() => setActive(null)} />
            </g>
          );
        })}
      </svg>
      <Tooltip tip={active === null ? null : tipFor(active)} width={width} />
    </div>
  );
}
